import React, { useCallback, useContext, useMemo, useState } from 'react';

import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import {
	AsaphDialog,
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import { EmptyResourceMessage } from '@/components/shared/EmptyResourceMessage';
import { SingleActionLayout } from '@/components/shared/SingleActionLayout/SingleActionLayout';
import { PANEL_TRANSITION_TIMEOUT } from '@/config/constants';
import {
	MessageType,
	useListMyChatsQuery,
	useSendChatMessageMutation,
} from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { DirectMessagesCard } from '@/pages/DirectMessages/components/DirectMessagesCard';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useUnreadChats } from '@/providers/UnreadChatsProvider/UnreadChatsProvider';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';

import { GenerateSetlistContext } from '../../GenerateSetlist';
import styles from './ShareDialog.module.scss';

type ShareDialogProps = {
	onClose: () => void;
};

const ShareDialog: React.FC<ShareDialogProps> = ({ onClose }) => {
	const { chatIds } = useUnreadChats();
	const { refetch } = useSetlists();
	const { setlist } = useContext(GenerateSetlistContext);
	const { handleError } = useErrorHandler();

	const { data: chatsData, isLoading } = useListMyChatsQuery({
		input: {
			limit: 1,
			offset: 0,
		},
	});
	const { mutateAsync: sendChatMessage } = useSendChatMessageMutation();

	const [selectedChat, setSelectedChat] = useState<string>();

	const chats = useMemo(() => chatsData?.listMyChats || [], [chatsData]);
	const sendMessage = useCallback(
		async (chatId?: string) => {
			try {
				if (!setlist?.id || !chatId) {
					throw new Error('Error sharing setlist');
				}
				const id = uuidv4();
				await sendChatMessage({
					chatId,
					input: {
						id,
						content: 'Shared setlist',
						type: MessageType.Setlist,
						metadata: JSON.stringify({
							setlistId: setlist.id,
						}),
					},
				});
				refetch();
				onClose();
			} catch (error) {
				handleError({
					errorCode: 'ShareSetlistError',
					error,
					friendlyMessage: `There was an issue with sharing this setlist. Please try again.`,
				});
			}
		},
		[handleError, refetch, sendChatMessage, setlist?.id, onClose],
	);

	const noChats = !chats.length;

	return (
		<AsaphDialog
			className={styles['root']}
			open={true}
			onClose={() => onClose()}
		>
			<div className={styles['toolbar']}>
				<AsaphDialogHeader
					leadingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: (
							<AsaphDialogHeaderButton
								type="text"
								label="Cancel"
								handlePress={() => onClose()}
							/>
						),
					}}
					title={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: 'Share setlist',
					}}
					trailingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: '',
					}}
				/>
			</div>
			<div className={styles['toolbar-divider']}>
				<hr />
			</div>
			<section className={styles['content']}>
				{/* <div
					className={classNames([
						styles['direct-messages'],
						noChats && styles['full-height'],
					])}
				>
					{!noChats ? (
						chats.map((chat) => (
							<DirectMessagesCard
								key={`chat-${chat.id}`}
								chat={chat}
								isUnread={chatIds.includes(chat.id)}
								noLink
								chatCardProps={{
									selectable: true,
									checked: selectedChat === chat.id,
									onClick: () => {
										setSelectedChat(chat.id);
									},
								}}
							/>
						))
					) : (
						<EmptyResourceMessage
							title="There are no messages"
							subtitle="Send a new message to an individual or group"
						/>
					)}
				</div> */}
				<SingleActionLayout
					actionDisabled={!selectedChat}
					actionLabel="Share"
					onActionClick={() => {
						sendMessage(selectedChat);
					}}
					contentClassName={styles['full-height']}
				>
					{!noChats ? (
						chats.map((chat) => (
							<DirectMessagesCard
								key={`chat-${chat.id}`}
								chat={chat}
								isUnread={chatIds.includes(chat.id)}
								noLink
								chatCardProps={{
									selectable: true,
									checked: selectedChat === chat.id,
									onClick: () => {
										setSelectedChat(chat.id);
									},
								}}
							/>
						))
					) : (
						<EmptyResourceMessage
							className=""
							title="There are no messages yet"
							subtitle="Send a new message to an individual or group from Messages"
						/>
					)}
				</SingleActionLayout>
			</section>
		</AsaphDialog>
	);
};

export default ShareDialog;
